import { motion } from "framer-motion";
import { AlertTriangle, Building2, Landmark, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LocalBodiesSection from "../components/LocalBodiesSection";
import StateRepresentativeGrid from "../components/StateRepresentativeGrid";
import { getImageUrl, states } from "../services/api";

const StatePage = () => {
  const { stateName } = useParams();
  const navigate = useNavigate();
  const [stateData, setStateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const fetchState = async () => {
      if (!stateName) {
        setError("Invalid state name.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await states.getByName(stateName);

        if (!active) {
          return;
        }

        setStateData({
          ...response.data,
          mps: Array.isArray(response.data?.mps) ? response.data.mps : [],
          mlas: Array.isArray(response.data?.mlas) ? response.data.mlas : [],
        });
        setError("");
      } catch (fetchError) {
        console.error("State fetch error:", fetchError);
        if (active) {
          setError("Unable to fetch state data right now.");
          setStateData(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchState();

    return () => {
      active = false;
    };
  }, [stateName]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 rounded-2xl bg-white px-6 py-4 shadow-sm border">
          <LoaderCircle className="h-5 w-5 animate-spin text-blue-600" />
          <span className="text-gray-700">Loading state representatives...</span>
        </div>
      </div>
    );
  }

  if (error || !stateData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-lg rounded-3xl border bg-white p-8 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">State data unavailable</h1>
          <p className="mt-2 text-gray-600">{error || "State data not found."}</p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 rounded-full bg-blue-600 px-5 py-2.5 text-white shadow hover:bg-blue-700"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const mpCount = stateData.mps?.length || 0;
  const mlaCount = stateData.mlas?.length || 0;
  const stateBanner =
    stateData.state_name?.toLowerCase() === "bihar"
      ? "/State_Banner/bihar-banner.png"
      : "/image/banner.jpg";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50">
      <section
        className="relative overflow-hidden px-6 py-20 text-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.45), rgba(15, 23, 42, 0.45)), url(${stateBanner})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <motion.h1
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-extrabold text-white md:text-5xl"
        >
          {stateData.state_name}
        </motion.h1>
        <p className="mx-auto mt-4 max-w-2xl text-base text-white/90 md:text-lg">
          {stateData.slogan || "Track your representatives, follow accountability, and raise local issues faster."}
        </p>
      </section>

      <div className="mx-auto -mt-12 max-w-6xl px-4 pb-12">
        <section className="rounded-3xl border bg-white p-6 shadow-xl">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <img
                src={getImageUrl(stateData.cm?.image_url, "image/default-avatar.png")}
                alt={stateData.cm?.name || "Chief Minister"}
                className="h-24 w-24 rounded-full border object-cover shadow-sm"
                onError={(event) => {
                  event.currentTarget.src = getImageUrl("image/default-avatar.png");
                }}
              />
              <div>
                <p className="text-sm uppercase tracking-wide text-gray-500">Chief Minister</p>
                <h2 className="text-2xl font-bold text-gray-900">
                  {stateData.cm?.name || "Not available"}
                </h2>
                <p className="text-sm text-gray-600">
                  {stateData.cm?.party || stateData.ruling_party || "Party not available"}
                </p>
                <p className="text-sm text-gray-500">
                  {stateData.cm?.constituency || "Constituency details unavailable"}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                navigate(
                  `/raise-issue?state=${encodeURIComponent(stateData.state_name)}&to=cm&name=${encodeURIComponent(
                    stateData.cm?.name || "",
                  )}`,
                )
              }
              className="rounded-full bg-yellow-500 px-6 py-3 font-semibold text-white shadow hover:bg-yellow-600"
            >
              Raise Issue to CM
            </button>
          </div>
        </section>

        <section className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3 text-gray-500">
              <Building2 className="h-5 w-5 text-orange-500" />
              <span className="text-sm font-medium">Ruling Party</span>
            </div>
            <p className="mt-3 text-xl font-bold text-gray-900">
              {stateData.ruling_party || "Not updated"}
            </p>
          </div>

          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3 text-gray-500">
              <Landmark className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">MPs Loaded</span>
            </div>
            <p className="mt-3 text-3xl font-bold text-gray-900">{mpCount}</p>
            <p className="text-sm text-gray-500">
              Total seats: {stateData.total_mp_seats || mpCount}
            </p>
          </div>

          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3 text-gray-500">
              <Landmark className="h-5 w-5 text-indigo-600" />
              <span className="text-sm font-medium">MLAs Loaded</span>
            </div>
            <p className="mt-3 text-3xl font-bold text-gray-900">{mlaCount}</p>
            <p className="text-sm text-gray-500">
              Total seats: {stateData.total_mla_seats || mlaCount}
            </p>
          </div>
        </section>

        <StateRepresentativeGrid mps={stateData.mps} mlas={stateData.mlas} />

        <LocalBodiesSection
          panchayats={stateData.panchayats || []}
          municipalities={stateData.municipalities || []}
        />

        <section className="mt-12 rounded-3xl border bg-white p-8 text-center shadow-sm">
          <h3 className="text-2xl font-semibold text-gray-900">
            Ready to raise a local issue?
          </h3>
          <p className="mt-2 text-gray-600">
            JanShakti will route it to the best available representative for this constituency.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/raise-issue?state=${encodeURIComponent(stateData.state_name)}`)}
            className="mt-6 rounded-full bg-blue-600 px-6 py-3 font-semibold text-white shadow hover:bg-blue-700"
          >
            Raise an Issue
          </button>
        </section>
      </div>
    </div>
  );
};

export default StatePage;
